<template>
    <div>
        <h5 v-if="localValue.length">
            File<template v-if="localValue.length > 1">s</template>
        </h5>

        <div v-for="file in localValue" :key="file.id">
            <!-- TODO: Finish -->
            <div
                class="d-flex justify-content-between align-items-center mb-2 gap-2"
            >
                <div class="file-name text-truncate">
                    {{ file.original_filename }}
                </div>

                <div class="d-flex">
                    <IconButton
                        v-if="!hideViewButton"
                        preset="view"
                        class="me-2"
                        @click="viewFile(file)"
                    />
                    <!-- Downloading disabled!
                    <IconButton
                        preset="download"
                        class="me-2"
                        @click="downloadFile(file.id)"
                    />
                    -->
                    <IconButton
                        v-if="!hideDeleteButton"
                        preset="delete"
                        @click="openModal(file)"
                    />
                </div>
            </div>
        </div>

        <ToolTip full-width>
            <template #tooltip-trigger>
                <UploadField
                    v-if="!hideUploadField"
                    :accept="accept"
                    :multiple="multiple"
                    :max-image-height="maxImageHeight"
                    :max-image-width="maxImageWidth"
                    :disabled="disabled"
                    :allow-public-access="allowPublicAccess"
                    v-on:uploaded="fileUploaded"
                    v-on:uploading="uploading = $event"
                >
                    <template #image-preview>
                        <slot name="image-preview"></slot>
                    </template>
                </UploadField>
            </template>
            <template v-if="disabled && disabledTooltip"  #tooltip-content>
                {{ disabledTooltip }}
            </template>
        </ToolTip>

        <!-- Delete Employee Modal -->
        <ConfirmModal
            :visible="isModalVisible"
            title="Delete File?"
            confirm-button-text="Delete File"
            @close="closeModal"
            @confirm="deleteImage"
        >
            Are you sure you want to delete
            <span class="fw-bold text-break">{{
                selectedFile.original_filename
            }}</span
            >? If you delete the file, you will lose it.
        </ConfirmModal>
    </div>
</template>

<script>
import UploadField from '@/components/fields/UploadField.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';
import IconButton from '@/components/IconButton.vue';
import ToolTip from './ToolTip.vue';

export default {
    name: 'AdvancedUploadField',
    components: {
        UploadField,
        ConfirmModal,
        IconButton,
        ToolTip,
    },
    props: {
        modelValue: {
            // Files
            type: null,
            default: [],
        },
        accept: {
            // 'image', '*' (any), or list of specific types (ex. ["jpg", "jpeg", "png"])
            type: [String, Array],
            default: '*',
        },
        multiple: {
            // allow uploading multiple files at a time
            type: Boolean,
            default: false,
        },
        maxImageWidth: {
            // max width (if accept == 'image')
            type: Number,
            default: null,
        },
        maxImageHeight: {
            // max height (if accept == 'image')
            type: Number,
            default: null,
        },
        disabled: {
            // input is disabled?
            type: Boolean,
            default: false,
        },
        disabledTooltip: {
            type: String,
            default: '',
        },
        hideUploadField: {
            // hide the upload field
            type: Boolean,
            default: false,
        },
        hideDeleteButton: {
            // hide the delete button
            type: Boolean,
            default: false,
        },
        hideViewButton: {
            // hide the view button
            type: Boolean,
            default: false,
        },
        useCustomPdfViewer: {
            // use the custom pdf viewer
            type: Boolean,
            default: false,
        },
        allowPublicAccess: {
            // if true, prevents app_admin_only from being set (for app-wide content like release notes)
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            localValue: [],
            isModalVisible: false,
            selectedFile: null,
            uploading: false,
            deleting: false,
        };
    },
    watch: {
        modelValue() {
            this.localValue = structuredClone(this.modelValue);
        },
        isBusy(newVal) {
            this.$emit('uploading', newVal);
        },
    },
    emits: ['update:modelValue', 'change', 'click', 'uploading'],
    computed: {
        isBusy() {
            return this.uploading || this.deleting;
        },
    },
    methods: {
        isImage(file) {
            return file.file_type.startsWith('image');
        },
        fileUploaded(fileResponse, file) {
            // If not multiple, and there is an existing file, replace the existing file with the new one
            if (!fileResponse.success) return;
            if (!this.multiple && this.localValue.length > 0) {
                // Delete the existing file first
                const existingFile = this.localValue[0];
                this.$deleteData('upload/delete', { id: existingFile.id }).then(
                    (response) => {
                        if (response.success) {
                            // Replace the existing file with the new one
                            this.localValue = [
                                {
                                    ...fileResponse.data,
                                    original_filename: file.name,
                                    file_type: file.type,
                                },
                            ];
                            this.change(this.localValue);
                        } else if ('message' in response) {
                            this.$alertStore.pushError(response.message);
                        }
                    }
                );
            } else {
                // Multiple is true or no existing files, just add the new file
                this.localValue.push({
                    ...fileResponse.data,
                    original_filename: file.name,
                    file_type: file.type,
                });
                this.change(this.localValue);
            }
        },

        change(newValue) {
            this.$emit('update:modelValue', newValue);
            this.$emit('change', newValue);
        },

        viewFile(file) {
            if (this.useCustomPdfViewer && file?.hash) {
                const routeData = this.$router.resolve({
                    name: 'PdfViewer',
                    params: {
                        hash: file.hash,
                    },
                });
                window.open(routeData.href, '_blank');
                return;
            }
            this.$getFileBlob({ id: file?.id }).then(([blob, filename]) => {
                const blobUrl = URL.createObjectURL(blob);
                window.open(blobUrl, '_blank');
            });
        },

        downloadFile(fileId) {
            this.$downloadFile({ id: fileId }).then(() => {
                // Downloaded! Nothing to display here
            });
        },

        deleteImage() {
            this.deleting = true;
            this.$deleteData('upload/delete', { id: this.selectedFile.id })
                .then((response) => {
                    if (response.success) {
                        const index = this.localValue.findIndex(
                            (file) => file.id == this.selectedFile.id
                        );
                        if (index != -1) {
                            this.localValue.splice(index, 1);
                            this.change(this.localValue);
                            this.$alertStore.pushSuccess('File deleted');
                        }
                    } else if ('message' in response) {
                        this.$alertStore.pushError(response.message);
                    }
                })
                .finally(() => {
                    this.closeModal();
                    this.deleting = false;
                });
        },
        openModal(file) {
            this.isModalVisible = true;
            this.selectedFile = file;
        },
        closeModal() {
            this.isModalVisible = false;
            this.selectedFile = null;
        },
    },
    mounted() {
        this.localValue = structuredClone(this.modelValue);
    },
};
</script>

<style scoped>
.file-name {
    font-size: 0.875rem;
    line-height: 1.7;
    color: var(--black);
}
</style>
